import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'

const buttonVariants = cva(
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl text-sm font-bold transition duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300/35 disabled:pointer-events-none disabled:opacity-50 active:translate-y-px',
  {
    variants: {
      variant: {
        default: 'bg-sky-300 text-slate-950 shadow-[0_16px_40px_rgba(56,189,248,0.16)] hover:bg-sky-200',
        outline: 'border border-slate-700 bg-slate-950/40 text-slate-100 hover:border-sky-300/70 hover:text-sky-100',
        ghost: 'text-slate-300 hover:bg-slate-900 hover:text-sky-100',
        inverse: 'bg-slate-950 text-white hover:bg-slate-800',
      },
      size: {
        default: 'px-5 py-3',
        sm: 'min-h-10 px-4 py-2',
        lg: 'min-h-12 px-6 py-4',
      },
    },
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

export function buttonClassName({ className, size, variant }: VariantProps<typeof buttonVariants> & { className?: string }) {
  return cn(buttonVariants({ size, variant }), className)
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, size, variant, ...props }, ref) => (
    <button ref={ref} className={buttonClassName({ className, size, variant })} {...props} />
  ),
)
Button.displayName = 'Button'
